import { Box, Button, Divider, Flex, Modal, SegmentedControl, SimpleGrid, Switch, Table, Text } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosCommonError } from '../../../../../../shared/types';
import { useState } from 'react';
import Swal from 'sweetalert2';
import { useDisclosure } from '@mantine/hooks';
import { showSuccessNotification } from '../../../../../ui/notification';
import { showNotification } from '@mantine/notifications';
import { handleError } from '../../../../../ui/err/handleError';
import { AxiosError } from 'axios';
import { companyApis } from '../../../../../../shared/apis/company/apis';
import type { IPostOpenApiKeyParams, IPutCompanyDetailParams } from '../../../../../../shared/apis/company/types';
import { queries } from '../../../../../../shared/queries';

interface IPropsServiceChannelPanel {
  companyInfo?: {
    companyId?: number;
    data?: {
      name?: string;
      email?: string;
      tel?: string;
    };
  };
  serviceChannel: string;
  config: IServiceChannelConfig[];
  serviceConfig: IServiceChannelConfig;
}

type ServiceChannelUseStatus = 'Y' | 'N';

type SubExtension = {
  subExtensionId: number;
  name?: string;
  used: ServiceChannelUseStatus;
};

type Extension = {
  extensionId: number;
  name: string;
  used?: ServiceChannelUseStatus;
  subExtensions: SubExtension[];
};

interface IServiceChannelConfig {
  used: ServiceChannelUseStatus;
  name: string;
  extension: Extension[];
  companyPrimaryUser: {
    email?: string;
  };
}

export type ServiceChannelSettings = {
  [channelName: string]: IServiceChannelConfig;
};

interface IAIAgentSubExtensionForm {
  machineData: {
    used: ServiceChannelUseStatus;
  };
  asCsHistory: {
    used: ServiceChannelUseStatus;
  };
  manual: {
    used: ServiceChannelUseStatus;
  };
}

interface IAIAgentForm {
  used: ServiceChannelUseStatus;
  subExtensions: [IAIAgentSubExtensionForm];
}

interface ServiceChannelFormValues {
  used: ServiceChannelUseStatus;
  AIAgent: IAIAgentForm;
  SMARTHACCP: ServiceChannelUseStatus;
  API: ServiceChannelUseStatus;
  SelfAI: ServiceChannelUseStatus;
}

const schema = yup.object().shape({
  used: yup.string().oneOf(['Y', 'N']).default('N'),
  AIAgent: yup.object().shape({
    used: yup.string().oneOf(['Y', 'N']).default('N'),
    subExtensions: yup.array().of(
      yup.object().shape({
        machineData: yup.object().shape({
          used: yup.string().oneOf(['Y', 'N']).default('N'),
        }),
        asCsHistory: yup.object().shape({
          used: yup.string().oneOf(['Y', 'N']).default('N'),
        }),
        manual: yup.object().shape({
          used: yup.string().oneOf(['Y', 'N']).default('N'),
        }),
      }),
    ),
  }),
  SMARTHACCP: yup.string().oneOf(['Y', 'N']).default('N'),
  // SelfAI: yup.string().oneOf(["Y", "N"]).default("N"),
  API: yup.string().oneOf(['Y', 'N']).default('N'),
});

function makeDefaultValues(serviceConfig: IServiceChannelConfig): ServiceChannelFormValues {
  // 초기값
  const defaultValues: ServiceChannelFormValues = {
    used: serviceConfig.used ?? 'N',
    AIAgent: {
      used: 'N',
      subExtensions: [
        {
          machineData: { used: 'N' },
          asCsHistory: { used: 'N' },
          manual: { used: 'N' },
        },
      ],
    },
    SMARTHACCP: 'N',
    SelfAI: 'N',
    API: 'N',
  };

  // AI Agent(1), 스마트 HACCP(2), SelfAI(3) 확장 기준 셋팅
  if (serviceConfig.used !== 'Y') {
    return defaultValues;
  }

  serviceConfig.extension.forEach((ext) => {
    if (ext.extensionId === 1) {
      // AI Agent
      defaultValues.AIAgent.used = ext.used ?? 'N';
      // subExtensionId 순서별로 used값 세팅
      // 초기값
      const agentDefault: IAIAgentSubExtensionForm = {
        machineData: { used: 'N' },
        asCsHistory: { used: 'N' },
        manual: { used: 'N' },
      };

      ext.subExtensions.forEach((sub) => {
        if (sub.subExtensionId === 1) agentDefault.machineData.used = sub.used ?? 'N';
        if (sub.subExtensionId === 2) agentDefault.asCsHistory.used = sub.used ?? 'N';
        if (sub.subExtensionId === 3) agentDefault.manual.used = sub.used ?? 'N';
      });
      defaultValues.AIAgent.subExtensions = [agentDefault];
    }
    if (ext.extensionId === 2) {
      defaultValues.SMARTHACCP = ext.used ?? 'N';
    }
    if (ext.extensionId === 3) {
      defaultValues.SelfAI = ext.used ?? 'N';
    }
    if (ext.extensionId === 4) {
      defaultValues.API = ext.used ?? 'N';
    }
  });

  return defaultValues;
}

export default function ServiceChannelPanel(props: IPropsServiceChannelPanel) {
  const { companyInfo, serviceChannel, serviceConfig } = props;
  const [isEdit, setIsEdit] = useState(false);
  const [selectPlan, setSelectPlan] = useState<string | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const defaultValues = makeDefaultValues(serviceConfig);
  const queryClient = useQueryClient();

  const { watch, control, handleSubmit, reset, setValue } = useForm<ServiceChannelFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const used = watch('used');

  const resetAdditionalServices = () => {
    setValue('AIAgent.used', 'N');
    setValue('AIAgent.subExtensions.0.machineData.used', 'N');
    setValue('AIAgent.subExtensions.0.asCsHistory.used', 'N');
    setValue('AIAgent.subExtensions.0.manual.used', 'N');
    setValue('SMARTHACCP', 'N');
  };

  const { data: apiServiceChannelPlan } = useQuery(['getApiServiceChannelPlan'], () => companyApis.getApiServiceChannelPlan(), {
    select: (data) => data.data.responses,
  });

  const { data: companyUsagePlan, refetch: refetchCompanyUsagePlan } = useQuery(
    ['getCompanyUsagePlan', companyInfo?.companyId],
    () => companyApis.getCompanyUsagePlan(companyInfo?.companyId ?? 0),
    {
      enabled: !!companyInfo?.companyId && companyInfo?.companyId > 0,
      select: (data) => data.data.responses,
    },
  );

  const { mutate: putCompanyDetail } = useMutation(
    ['putCompanyDetail'],
    (data: IPutCompanyDetailParams) => companyApis.putCompanyDetail(data),
    {
      onError: (error) => {
        handleError(error as AxiosError<AxiosCommonError>);
      },
    },
  );

  const { mutate: mutatePostOpenApiKey } = useMutation(
    ['postOpenApiKey'],
    (data: IPostOpenApiKeyParams) => companyApis.postOpenApiKey(data),
    {
      onSuccess: () => {
        showSuccessNotification('API Key가 발급되었습니다.');
        refetchCompanyUsagePlan();
        setSelectPlan(null);
        close();
      },
      onError: (error) => {
        setSelectPlan(null);
        showNotification({
          title: 'API Key 발급 실패',
          message: 'API Key 발급에 실패했습니다. 다시 시도해주세요.',
          color: 'red',
        });
      },
    },
  );

  // 서비스 채널 별 설정 저장
  const onSubmit = (data: ServiceChannelFormValues) => {
    const updatedData = {
      name: serviceChannel,
      used: data.used,
      extension:
        data.used === 'Y'
          ? [
              {
                extensionId: data.AIAgent.used === 'Y' ? 1 : undefined,
                subExtensions: [
                  data.AIAgent.subExtensions[0].machineData.used === 'Y' && {
                    subExtensionId: 1,
                  },
                  data.AIAgent.subExtensions[0].asCsHistory.used === 'Y' && {
                    subExtensionId: 2,
                  },
                  data.AIAgent.subExtensions[0].manual.used === 'Y' && {
                    subExtensionId: 3,
                  },
                ].filter(Boolean),
              },
              {
                extensionId: data.SMARTHACCP === 'Y' ? 2 : undefined,
                subExtensions: [],
              },
            ].filter((ext) => ext.extensionId !== undefined)
          : [],
    };

    const sendData = {
      companyId: companyInfo?.companyId ?? 0,
      data: {
        channels: updatedData,
      },
    };

    putCompanyDetail(sendData as any, {
      onSuccess: () => {
        reset(data);
        setIsEdit(false);
        queryClient.invalidateQueries({ queryKey: queries.company.details._def });
      },
    });
  };

  const handleCancelEdit = () => {
    reset();
    setIsEdit(false);
  };

  const maskApiKey = (apiKey: string | undefined | null): string => {
    if (!apiKey || apiKey.length <= 4) {
      return apiKey || 'N/A';
    }
    return apiKey.slice(0, 4) + '*'.repeat(apiKey.length - 4);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex justify="space-between" align="center" py="xs">
          <Text>{serviceChannel} 설정</Text>
          {serviceChannel !== 'API' && (
            <>
              {isEdit ? (
                <Flex gap="xs">
                  <Button type="submit">저장</Button>
                  <Button type="button" variant="outline" onClick={handleCancelEdit}>
                    취소
                  </Button>
                </Flex>
              ) : (
                <Button onClick={() => setIsEdit(true)}>수정</Button>
              )}
            </>
          )}
          {serviceChannel === 'API' && (
            <Box>
              <Button
                disabled={companyUsagePlan.filter((el: any) => el.usagePlanApiKeyStatus === 'ACTIVE').length >= 1}
                onClick={open}
              >
                API Key 발급
              </Button>
            </Box>
          )}
        </Flex>
        {serviceChannel !== 'API' && (
          <Flex align="center" py="xs">
            <Text fw={500} w={120} size="sm">
              채널 사용 여부
            </Text>
            <Controller
              name="used"
              control={control}
              render={({ field }) => (
                <SegmentedControl
                  disabled={!isEdit}
                  value={field.value === 'Y' ? 'Y' : 'N'}
                  onChange={(value) => {
                    field.onChange(value);
                    resetAdditionalServices();
                  }}
                  data={[
                    {
                      value: 'Y',
                      label: '사용',
                    },
                    {
                      value: 'N',
                      label: '미사용',
                    },
                  ]}
                />
              )}
            />
          </Flex>
        )}
        {(serviceChannel === 'SCAUTR' || serviceChannel === 'PROGIX') && used === 'Y' && (
          <>
            <Text py="xs" fw={500} w={120} size="sm">
              부가 서비스 설정
            </Text>
            <Divider />
            <Box px="xs" py="md">
              <Controller
                name="AIAgent.used"
                control={control}
                render={({ field }) => (
                  <Switch
                    disabled={!isEdit}
                    label="AI Agent"
                    checked={field.value === 'Y'}
                    {...field}
                    onChange={(e) => {
                      if (!e.currentTarget.checked) {
                        setValue('AIAgent.subExtensions.0.machineData.used', 'N');
                        setValue('AIAgent.subExtensions.0.asCsHistory.used', 'N');
                        setValue('AIAgent.subExtensions.0.manual.used', 'N');
                      }
                      field.onChange(e.currentTarget.checked ? 'Y' : 'N');
                    }}
                  />
                )}
              />
              {/* <Collapse in={true}>
                  <Box py="sm" px="lg">
                    <Text size="sm" c="dimmed">
                      사용할 부가 서비스 항목을 선택하세요.
                    </Text>
                    <Space h="md" />
                    <Flex gap="xl">
                      <Controller
                        name="AIAgent.subExtensions.0.machineData.used"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            label="기계 데이터"
                            disabled={!isEdit}
                            checked={field.value === "Y"}
                            {...field}
                            onChange={(e) => {
                              if (e.currentTarget.checked) {
                                setValue("AIAgent.used", "Y");
                              }
                              field.onChange(
                                e.currentTarget.checked ? "Y" : "N"
                              );
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="AIAgent.subExtensions.0.asCsHistory.used"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            disabled={!isEdit}
                            label="AS/CS 이력"
                            checked={field.value === "Y"}
                            {...field}
                            onChange={(e) => {
                              if (e.currentTarget.checked) {
                                setValue("AIAgent.used", "Y");
                              }
                              field.onChange(
                                e.currentTarget.checked ? "Y" : "N"
                              );
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="AIAgent.subExtensions.0.manual.used"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            disabled={!isEdit}
                            label="매뉴얼"
                            checked={field.value === "Y"}
                            {...field}
                            onChange={(e) => {
                              if (e.currentTarget.checked) {
                                setValue("AIAgent.used", "Y");
                              }
                              field.onChange(
                                e.currentTarget.checked ? "Y" : "N"
                              );
                            }}
                          />
                        )}
                      />
                    </Flex>
                  </Box>
                </Collapse> */}
            </Box>
            <Divider />
            {serviceChannel === 'PROGIX' && (
              <Box px="xs" py="md">
                <Controller
                  name="SMARTHACCP"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      disabled={!isEdit}
                      label="스마트 HACCCP"
                      checked={field.value === 'Y'}
                      {...field}
                      onChange={(e) => field.onChange(e.currentTarget.checked ? 'Y' : 'N')}
                    />
                  )}
                />
              </Box>
            )}
          </>
        )}
        {serviceChannel === 'API' && (
          <Table mt="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Plan</Table.Th>
                <Table.Th>API Key</Table.Th>
                <Table.Th>상태</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {companyUsagePlan && companyUsagePlan.length > 0 ? (
                companyUsagePlan.map((plan: any) => (
                  <Table.Tr key={plan.apiKey}>
                    <Table.Td>{plan.openApiUsagePlanDescription}</Table.Td>
                    <Table.Td>{maskApiKey(plan.apiKey)}</Table.Td>
                    <Table.Td>{plan.usagePlanApiKeyStatus}</Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                    데이터가 없습니다.
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </form>
      <Modal.Stack>
        <Modal opened={opened} onClose={close} title="Plan 선택" centered>
          <SimpleGrid cols={3} spacing="md">
            {apiServiceChannelPlan.map((plan: { openApiUsageName: string; openApiUsageId: number; description: string }) => (
              <Flex
                onClick={() => setSelectPlan(plan.openApiUsageId.toString())}
                style={(theme) => ({
                  border:
                    selectPlan === plan.openApiUsageId.toString()
                      ? `2px solid ${theme.colors.neutral[4]}`
                      : `1px solid ${theme.colors.gray[3]}`,
                  cursor: 'pointer',
                })}
                py={'md'}
                id={String(plan.openApiUsageId)}
                key={plan.openApiUsageId}
                direction="column"
                mb="md"
                gap={8}
              >
                <Text align="center" fw={500}>
                  {plan.openApiUsageName}
                </Text>
                <Text align="center">
                  {plan.description === 'Basic Plan' ? '$0.36/월' : plan.description === 'Standard Plan' ? '$2.1/월' : '$21/월'}
                </Text>
                <Text align="center" size="sm" c="dimmed">
                  {plan.description === 'Basic Plan'
                    ? '50,000 요청'
                    : plan.description === 'Standard Plan'
                      ? '300,000 요청'
                      : '3,000,000 요청'}
                </Text>
              </Flex>
            ))}
          </SimpleGrid>
          <Flex justify="flex-end" gap="md">
            <Button
              onClick={() => {
                setSelectPlan(null);
                close();
              }}
              variant="outline"
            >
              취소
            </Button>
            <Button
              disabled={!selectPlan}
              onClick={() => {
                Swal.fire({
                  icon: 'question',
                  title: 'API Key 발급',
                  text: '선택한 Plan으로 API Key를 발급하시겠습니까?',
                  showCancelButton: true,
                  confirmButtonText: '확인',
                  cancelButtonText: '취소',
                }).then((result) => {
                  if (result.isConfirmed) {
                    mutatePostOpenApiKey({
                      companyId: companyInfo?.companyId ?? 0,
                      usagePlanId: Number(selectPlan),
                    });
                  }
                });
              }}
            >
              설정하기
            </Button>
          </Flex>
        </Modal>
      </Modal.Stack>
    </>
  );
}
